import { useCallback, useEffect, useLayoutEffect } from "react";
import { create } from "zustand";

// W-8af4eaef (the principal, 8 Oct 2026): "when I hit approve, I don't really
// know that I hit it … it takes a second or two". Every approval card (the
// conversation's composer card on every engine, and the World card in the
// Inbox and the agent strip) shows the press here, in the same commit as the
// click: the pressed button says "Approving…" or "Declining…", the card's
// decisions are disabled and the card dims. The press lasts until the card
// leaves (its decision landed) or the decision is refused, when the card is
// back as it was and its own error surface says why.

/** Which of a card's decisions the person pressed. */
export type PressedDecision = "approve" | "decline";

/** The pressed button's words while its decision is on its way. */
export const PRESSED_LABEL: Readonly<Record<PressedDecision, string>> = {
  approve: "Approving…",
  decline: "Declining…",
};

/** A pressed card's body: dimmed at once while its decision is on its way. */
export const PRESSED_CARD_CLASS = "opacity-60 transition-opacity";

/** One card's key, by where its decision goes: a provider's request or a World gate instance. */
export const approvalPressKey = (lane: "provider" | "gate", id: string): string => `${lane}:${id}`;

export interface ApprovalPress {
  readonly decision: PressedDecision;
  /** The button pressed, when a card has two that approve (once, and for the session). */
  readonly button: string;
}

interface ApprovalPressState {
  readonly presses: Readonly<Record<string, ApprovalPress>>;
}

const useApprovalPressStore = create<ApprovalPressState>()(() => ({ presses: {} }));

/**
 * How many of a card's decision rows are drawn. The press is keyed by the
 * card (its lane and request), not by the component, so a layout switch that
 * draws the card's buttons again (the composer's collapsed and full layouts)
 * keeps it; it is forgotten once no row of that card is drawn.
 */
const drawn = new Map<string, number>();
/** The refusal each drawn card last showed: only a new one releases its press. */
const shownRefusal = new Map<string, string | null>();

function releasePress(cardKey: string): void {
  useApprovalPressStore.setState((state) => {
    if (state.presses[cardKey] === undefined) return state;
    const { [cardKey]: _released, ...presses } = state.presses;
    return { presses };
  });
}

/** The press on a card, for the parts of it that do not hold its buttons (its body dims). */
export function useApprovalPressed(cardKey: string | null): ApprovalPress | null {
  return useApprovalPressStore((state) =>
    cardKey === null ? null : (state.presses[cardKey] ?? null),
  );
}

/**
 * The card's decisions hold one press at a time. `press` records it at once
 * and answers false when the card is already deciding (nothing is sent
 * twice); `release` returns the card to as it was. A refusal the card shows
 * (`refusal` turning non-null) releases it before that refusal is painted, and
 * a card that leaves or changes request forgets its press.
 */
export function useApprovalPress(cardKey: string | null, refusal: string | null = null) {
  const pressed = useApprovalPressed(cardKey);
  const press = useCallback(
    (decision: PressedDecision, button: string = decision): boolean => {
      if (cardKey === null || useApprovalPressStore.getState().presses[cardKey] !== undefined) {
        return false;
      }
      useApprovalPressStore.setState((state) => ({
        presses: { ...state.presses, [cardKey]: { decision, button } },
      }));
      return true;
    },
    [cardKey],
  );
  const release = useCallback(() => {
    if (cardKey !== null) releasePress(cardKey);
  }, [cardKey]);
  // A refusal that arrives releases, before it is painted. One the card showed
  // before the press (or before a layout switch drew its row again) does not.
  useLayoutEffect(() => {
    if (cardKey === null) return;
    const before = shownRefusal.get(cardKey);
    shownRefusal.set(cardKey, refusal);
    if (refusal !== null && refusal !== before) releasePress(cardKey);
  }, [cardKey, refusal]);
  useEffect(() => {
    if (cardKey === null) return;
    drawn.set(cardKey, (drawn.get(cardKey) ?? 0) + 1);
    return () => {
      const left = (drawn.get(cardKey) ?? 1) - 1;
      if (left > 0) {
        drawn.set(cardKey, left);
        return;
      }
      drawn.delete(cardKey);
      // A row drawn again in the same commit (a layout switch) keeps the press.
      queueMicrotask(() => {
        if (drawn.has(cardKey)) return;
        shownRefusal.delete(cardKey);
        releasePress(cardKey);
      });
    };
  }, [cardKey]);
  return { pressed, press, release };
}

/**
 * A decision the lane refused, or that was never sent: an atom command's
 * Failure, or no result (no active conversation, or an answer already on its way).
 */
export function decisionRefused(result: unknown): boolean {
  if (result === undefined) return true;
  return (
    typeof result === "object" &&
    result !== null &&
    "_tag" in result &&
    (result as { readonly _tag: unknown })._tag === "Failure"
  );
}
